/**
 * queueSearch — the pure half of "type a job # or task # and the queue finds it
 * even when it is months out".
 *
 * GET /queue/{workstation} only returns Pending tasks scheduled or due within
 * horizon_days (default 30) — the cap that stopped the queue flooding and timing
 * out. While the shop builds ahead for Christmas, operators work jobs due months
 * away: task 39044 (job #9880, Fabrication/Welding, scheduled 2026-10-29, due
 * 2027-01-29) is absent from the default 216-row Fabrication queue and present
 * only at horizon_days=365 (388 rows). It is not blocked and passes "Ready only";
 * the horizon is the ONLY thing hiding it.
 *
 * The fix (Mike's idea): an exact job or task number in the search box asks the
 * API for that one job/task at this station with NO date horizon
 * (`?find=<n>`), and the page merges the answer into the list it already has.
 * The default queue never changes. This module holds the pieces that need no
 * React and no fetch, so they can be tested:
 *
 *   parseQueueSearchNumber  — "39044" / "#9880" / " 39044 " -> the integer;
 *                             anything else (too short, letters) -> null.
 *   rowMatchesNumber        — is this row THE job/task asked for? Used to trim an
 *                             API that ignores `find` (it returns the ordinary
 *                             30-day queue) and to check the local list.
 *   mergeLookupIntoQueue    — lookup items not already in the list, appended and
 *                             marked found_beyond_window; the list's own copy
 *                             always wins on a key collision.
 *   laserQueueKey / taskQueueKey — identity, mirroring how the page builds list
 *                             items (Laser groups by job + cut sheet; every other
 *                             station is one item per task).
 */

// 3 to 9 digits, optional leading "#" (and space after it). Two digits is a
// priority or a quantity, not a job or task number — job numbers are 4 digits,
// task ids 5 — and 10+ digits is not an INTEGER column.
const QUEUE_NUMBER_RE = /^#?\s*(\d{3,9})$/;

/** The exact job/task number a search term names, or null when it names none. */
export function parseQueueSearchNumber(term) {
  if (term == null) return null;
  const m = QUEUE_NUMBER_RE.exec(String(term).trim());
  return m ? parseInt(m[1], 10) : null;
}

const asInt = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
};

/**
 * True when `row` is task #n or belongs to job #n. Works on a raw /queue row
 * (task_id, master_job_num) and on a grouped Laser item (also checks tasks[]).
 * jobs.master_job_num and job_tasks.task_id are INTEGER, but the coercion costs
 * nothing and a stringified id would otherwise silently match nothing.
 */
export function rowMatchesNumber(row, n) {
  if (!row || n == null) return false;
  if (asInt(row.task_id) === n) return true;
  if (asInt(row.master_job_num) === n) return true;
  return Array.isArray(row.tasks) && row.tasks.some((t) => t && asInt(t.task_id) === n);
}

/** Keep only the rows that are job/task #n (defends against an API without `find`). */
export function filterLookupRows(rows, n) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((r) => rowMatchesNumber(r, n));
}

/** Non-Laser list items are one per task. */
export function taskQueueKey(item) {
  return `task-${item?.task_id}`;
}

/**
 * Laser list items come out of groupLaserJobs, which keeps a row individual when
 * it has a part_id (shape stock) or has neither part nor cut sheet (maintenance /
 * generic), and groups the rest by job + cut sheet. The grouped object spreads
 * its primary row, so the same three fields decide identity here.
 */
export function laserQueueKey(item) {
  if (!item) return "task-undefined";
  if (item.part_id == null && item.cut_sheet_id != null) {
    return `sheet-${item.job_id}-${item.cut_sheet_id}`;
  }
  return `task-${item.task_id}`;
}

/**
 * The list the page will filter and sort: `jobs` as-is, then every lookup item
 * whose key is not already present, marked `found_beyond_window: true`. A row
 * the queue already has is never replaced by its lookup twin — the main list is
 * fresher (loadQueue is what actions and the 5-minute refresh reload) and its
 * copy carries the page's own side effects (blocker, maintenance-today).
 */
export function mergeLookupIntoQueue(jobs, lookup, keyFn) {
  const base = Array.isArray(jobs) ? jobs : [];
  if (!Array.isArray(lookup) || lookup.length === 0) return [...base];
  const seen = new Set(base.map(keyFn));
  const merged = [...base];
  for (const item of lookup) {
    if (!item) continue;
    const key = keyFn(item);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push({ ...item, found_beyond_window: true });
  }
  return merged;
}
